import React, { useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { CheckCircle, Calendar, Clock, MapPin, Phone, Mail } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Navbar } from '../components/layout/Navbar';
import { Footer } from '../components/layout/Footer';
import { SEO } from '../components/SEO';

/**
 * AnmeldungErfolgreichPage - Erfolgsseite nach Kursbuchung
 * 
 * Diese Seite dient als Conversion-Ziel für Google Ads Tracking.
 * Die Conversion wird automatisch beim Laden der Seite ausgelöst.
 */
const AnmeldungErfolgreichPage = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  
  // URL Parameter für Buchungsdetails
  const name = searchParams.get('name') || '';
  const kurs = searchParams.get('kurs') || 'Erste Hilfe Kurs';
  const datum = searchParams.get('datum') || '';
  const uhrzeit = searchParams.get('uhrzeit') || '09:00 - 16:30 Uhr';
  const betrag = searchParams.get('betrag') || '';
  const zahlungsart = searchParams.get('zahlungsart') || '';

  // Google Ads Conversion Tracking beim Laden der Seite
  useEffect(() => {
    // Google Ads Conversion Event auslösen
    // ID: AW-453336545 | Label: CS5JCIau-4bE0G7ldgB
    if (typeof window.gtag === 'function') {
      window.gtag('event', 'conversion', {
        'send_to': 'AW-453336545/CS5JCIau-4bE0G7ldgB',
        'value': betrag ? parseFloat(betrag) : 40.00,
        'currency': 'EUR',
        'transaction_id': `${Date.now()}`
      });
      console.log('Google Ads Conversion: AW-453336545/CS5JCIau-4bE0G7ldgB');
    }
  }, [betrag]);

  return (
    <div className="min-h-screen bg-white flex flex-col">
      <SEO 
        page="termine"
        customTitle="Anmeldung erfolgreich | Erste Hilfe Bottrop"
        customDescription="Vielen Dank für Ihre Anmeldung zum Erste-Hilfe-Kurs in Bottrop."
      />
      <Navbar />
      
      <main className="flex-grow py-12 md:py-16">
        <div className="max-w-2xl mx-auto px-4">
          
          {/* Erfolgs-Header */}
          <div className="text-center mb-10">
            <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <CheckCircle className="w-10 h-10 text-green-600" />
            </div>
            <h1 className="text-3xl font-bold text-[#1e3a5f] mb-4">
              Anmeldung erfolgreich!
            </h1>
            <p className="text-lg text-slate-600">
              Vielen Dank für Ihre Buchung. Sie erhalten in Kürze eine Bestätigung per E-Mail.
            </p>
          </div>

          {/* Buchungsdetails */}
          {(name || datum) && (
            <Card className="bg-slate-50 border-0 mb-8">
              <CardContent className="p-6">
                <h2 className="font-semibold text-[#1e3a5f] text-lg mb-4">Ihre Buchungsdetails</h2>
                <div className="space-y-3">
                  {name && (
                    <div className="flex justify-between items-center py-2 border-b border-slate-200">
                      <span className="text-slate-600">Name:</span>
                      <span className="font-medium text-[#1e3a5f]">{name}</span>
                    </div>
                  )}
                  <div className="flex justify-between items-center py-2 border-b border-slate-200">
                    <span className="text-slate-600">Kurs:</span>
                    <span className="font-medium text-[#1e3a5f]">{kurs}</span>
                  </div>
                  {datum && (
                    <div className="flex justify-between items-center py-2 border-b border-slate-200">
                      <span className="text-slate-600 flex items-center gap-2">
                        <Calendar className="w-4 h-4" /> Datum:
                      </span>
                      <span className="font-medium text-[#1e3a5f]">{datum}</span>
                    </div>
                  )}
                  <div className="flex justify-between items-center py-2 border-b border-slate-200">
                    <span className="text-slate-600 flex items-center gap-2">
                      <Clock className="w-4 h-4" /> Uhrzeit:
                    </span>
                    <span className="font-medium text-[#1e3a5f]">{uhrzeit}</span>
                  </div>
                  {betrag && (
                    <div className="flex justify-between items-center py-2">
                      <span className="text-slate-600">Betrag:</span>
                      <span className="font-bold text-green-600">{betrag} €</span>
                    </div>
                  )}
                  {zahlungsart && (
                    <div className="flex justify-between items-center py-2 border-t border-slate-200">
                      <span className="text-slate-600">Zahlungsart:</span>
                      <span className="font-medium text-[#1e3a5f]">{zahlungsart}</span>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Kursort */}
          <Card className="bg-blue-50 border-0 mb-8">
            <CardContent className="p-6">
              <h2 className="font-semibold text-[#1e3a5f] text-lg mb-4 flex items-center gap-2">
                <MapPin className="w-5 h-5 text-blue-600" />
                Kursort
              </h2>
              <div className="text-slate-700">
                <p className="font-medium">Erste Hilfe Bottrop – Aktiv helfen</p>
                <p>Poststraße 6</p>
                <p>46236 Bottrop</p>
              </div>
              <a 
                href="https://maps.google.com/?q=Poststraße+6,+46236+Bottrop"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-block mt-4 text-blue-600 hover:underline text-sm"
              >
                → Route in Google Maps öffnen
              </a>
            </CardContent>
          </Card>

          {/* Wichtige Hinweise */}
          <Card className="bg-amber-50 border border-amber-200 mb-8">
            <CardContent className="p-6">
              <h2 className="font-semibold text-amber-800 text-lg mb-3">Bitte beachten Sie:</h2>
              <ul className="text-amber-700 space-y-2 text-sm">
                <li>• Bringen Sie bitte einen gültigen Lichtbildausweis mit</li>
                <li>• Erscheinen Sie bitte pünktlich zum Kursbeginn</li>
                <li>• Der Kurs dauert 7,5 Stunden inkl. Pausen</li>
                <li>• Ihre Bescheinigung erhalten Sie direkt nach dem Kurs</li>
              </ul>
            </CardContent>
          </Card>

          {/* Kontakt bei Fragen */}
          <Card className="bg-slate-100 border-0 mb-8">
            <CardContent className="p-6">
              <h2 className="font-semibold text-[#1e3a5f] text-lg mb-4">Fragen zur Buchung?</h2>
              <div className="space-y-3">
                <a href="tel:020419878666" className="flex items-center gap-3 text-slate-700 hover:text-[#1e3a5f]">
                  <Phone className="w-5 h-5 text-red-600" />
                  <span>02041 9878 666</span>
                </a>
                <a href="mailto:info@erstehilfebottrop.de" className="flex items-center gap-3 text-slate-700 hover:text-[#1e3a5f]">
                  <Mail className="w-5 h-5 text-red-600" />
                  <span>info@erstehilfebottrop.de</span>
                </a>
              </div>
            </CardContent>
          </Card>

          {/* Zurück-Button */}
          <div className="text-center">
            <Button 
              onClick={() => navigate('/')}
              className="bg-[#1e3a5f] hover:bg-[#2d4a6f] text-white px-8 py-3"
            >
              Zurück zur Startseite
            </Button>
          </div>

        </div>
      </main>

      <Footer />
    </div>
  );
};

export default AnmeldungErfolgreichPage;
